import { useState, useEffect } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { TextField } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { APIPATH } from '../App';

function Login() {
    const navigate = useNavigate();
    const location = useLocation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [errorInfo, setErrorInfo] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [step, setStep] = useState<"credentials" | "2fa">("credentials");

    const [checkingAuth, setCheckingAuth] = useState(true);

    useEffect(() => {
    const checkLoggedIn = async () => {
        try {
        const res = await fetch(APIPATH + "/Auth/currentUser", {
            method: "GET",
            credentials: "include",
        });

        if (res.ok) {
            navigate("/project-admin", { replace: true });
            return;
        }
        } catch {
        // ignore
        } finally {
        setCheckingAuth(false);
        }
    };

    checkLoggedIn();
    }, [navigate]);


    useEffect(() => {
        const state = location.state as { passwordReset?: boolean } | null;

        if (state?.passwordReset) {
            setInfoMessage('Your password has been changed. Please log in with your new password.');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const handleSubmitCredentials = async (e: any) => {
        e.preventDefault();
        setError(null);
        setInfoMessage(null);
        setIsSubmitting(true);

        try {
            const response = await fetch(APIPATH + '/Auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    username,
                    password
                })
            });

            if (!response.ok) {
                setError('Invalid username or password, either your credentials are incorrect or your account has been disabled.');
                setErrorInfo('Email john.heseltine@ociusconsulting.co.uk" if you have any questions.');
                setIsSubmitting(false);
                return;
            }

            const data = await response.json(); 

            if (data.success) {
                setStep("2fa");
            } else {
                setError(data.error ?? "Login failed.");
            }
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit2FA = async (e: any) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            const response = await fetch(APIPATH + '/Auth/verify-2fa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username, code })
            });

            const data = await response.json();
            if (!data.success) {
                setError(data.error ?? "Invalid verification code.");
                setIsSubmitting(false);
                return;
            }

            navigate('/projects', { replace: true });
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (checkingAuth) return null;

    return (
       
    <div className="main pb-4 d-flex flex-column align-items-center">
            <h2 className="mb-3">
                {step === "credentials" ? "Sign in to Our Journey" : "Enter your verification code"}
            </h2>

            {infoMessage && step === "credentials" && (
                <p style={{ color: 'green', marginBottom: '1rem' }}>
                    {infoMessage}
                </p>
            )}

            {step === "credentials" && (
                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center"
                }}>
                    <form onSubmit={handleSubmitCredentials} style={{ width: "350px" }}>
                        <div className="mb-3">
                            <TextField
                                id="username-field"
                                fullWidth
                                label="Email"
                                variant="outlined"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <TextField
                                id="password-field"
                                fullWidth
                                label="Password"
                                type="password"
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        
                        {error && (
                            <p style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                {error}
                            </p>
                        )}

                        {errorInfo && (
                            <p style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                {errorInfo}
                            </p>
                        )}
                        
                    <div className="mb-3 d-flex justify-content-center">
                            <Button
                                id="login-button"
                                type="submit"
                                className="styled-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Logging in…' : 'Log in'}
                            </Button>
                        </div>

                        <p className="mt-2 text-center">
                            <a href="/forgot-password" style={{ textDecoration: 'underline' }} id="forgot-password-link">
                                Forgot your password?
                            </a>
                        </p>
                        <p className="mt-2 text-center">
                            <a href="/TermsAndConditions.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }} id="terms-link">
                                By using Our Journey, you agree to our Terms and Conditions.
                            </a>
                        </p>
                    </form>
                </div>
            )}

            {step === "2fa" && (
                <form onSubmit={handleSubmit2FA}>
                    <p className="mb-3">
                        An 8-digit verification code has been sent to your email.  
                        Enter it below to finish signing in.
                    </p>

                    <div className="mb-3">
                        <TextField
                            id="verification-code-field"
                            fullWidth
                            label="Verification code"
                            autoComplete="one-time-code"
                            variant="outlined"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                        />
                    </div>

                    {error && (
                        <p style={{ color: 'red', marginBottom: '1rem' }}>
                            {error}
                        </p>
                    )}

                    <Button
                        type="submit"
                        className="styled-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Verifying…' : 'Verify code'}
                    </Button>
                </form>
            )}
        </div>
    );
}

export default Login;
